import { type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export type BadgeVariant = 'ok' | 'warning' | 'critical' | 'neutral' | 'success';
type BadgeSize = 'sm' | 'md';

export interface BadgeProps {
  variant: BadgeVariant;
  size?: BadgeSize;
  /** Optional dot indicator */
  dot?: boolean;
  className?: string;
  children: ReactNode;
}

const variantStyles: Record<BadgeVariant, string> = {
  ok: 'bg-status-ok-bg text-status-ok-text border-status-ok/20',
  warning: 'bg-status-warning-bg text-status-warning-text border-status-warning/20',
  critical: 'bg-status-critical-bg text-status-critical-text border-status-critical/20',
  neutral: 'bg-gray-100 text-gray-700 border-gray-200',
  success: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

const dotStyles: Record<BadgeVariant, string> = {
  ok: 'bg-status-ok',
  warning: 'bg-status-warning',
  critical: 'bg-status-critical',
  neutral: 'bg-gray-400',
  success: 'bg-emerald-500',
};

const sizeStyles: Record<BadgeSize, string> = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
};

export function Badge({ variant, size = 'sm', dot, className, children }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5',
        'font-medium rounded-full border',
        variantStyles[variant],
        sizeStyles[size],
        className
      )}
    >
      {dot && (
        <span
          className={cn(
            'w-1.5 h-1.5 rounded-full shrink-0',
            dotStyles[variant]
          )}
        />
      )}
      {children}
    </span>
  );
}
